import React, { useEffect, useState } from "react";
import styles from "./HalloweenDecor.module.css";
import SeasonalLottie, { LEAVES_PATH } from "./SeasonalLottie";
import HangingSpider from "./HangingSpider";
import { FloatingGhost } from "./HalloweenGhosts";

// SCRUM-922: Halloween Sign-in theme (Figma node 7501:13721 inside frame
// 2147224381). Everything here is decorative: aria-hidden, pointer-events:
// none (see the CSS), so the sign-in form underneath keeps working exactly as
// before. Artwork is exported from Figma into /public/assets/images/halloween
// and only fetched while the theme is active.
const IMG = "/assets/images/halloween";

// True while the pointer is over (or keyboard focus is on) the form's Sign in
// button. Read-only: it only listens to document events, never the form.
const useSignInAnticipation = () => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const isSignIn = (el) =>
      el instanceof Element && !!el.closest('form button[type="submit"]');
    const enter = (e) => isSignIn(e.target) && setOn(true);
    const leave = (e) =>
      isSignIn(e.target) && !isSignIn(e.relatedTarget) && setOn(false);
    document.addEventListener("mouseover", enter);
    document.addEventListener("mouseout", leave);
    document.addEventListener("focusin", enter);
    document.addEventListener("focusout", leave);
    return () => {
      document.removeEventListener("mouseover", enter);
      document.removeEventListener("mouseout", leave);
      document.removeEventListener("focusin", enter);
      document.removeEventListener("focusout", leave);
    };
  }, []);
  return on;
};

// Page-level layer: falling leaves (autumn-leaves-falling.lottie, scaled to
// cover the page), the corner cobweb, leaves lying on the ground, a low mist
// and pumpkins/candy along the bottom edge. The jack-o'-lanterns glow
// brighter while the user is about to press Sign in. Hidden below 900px
// (mobile keeps the card theme only).
export const HalloweenPageDecor = () => {
  const anticipate = useSignInAnticipation();
  return (
    <div
      className={`${styles.pageLayer} ${anticipate ? styles.anticipate : ""}`}
      aria-hidden="true"
    >
      <SeasonalLottie
        path={LEAVES_PATH}
        className={styles.leaves}
        fit="xMidYMid slice"
      />

      <FloatingGhost />

      <img
        src={`${IMG}/cobweb-corner.svg`}
        alt=""
        className={styles.cobwebCorner}
      />
      <img
        src={`${IMG}/leaf-maple-red.svg`}
        alt=""
        className={styles.leafRestRight}
      />

      {/* Static leaves lying along the bottom (Figma 7505:19195 / 18060 /
        16399 / 21465), always visible under the falling ones; painted before the
        pumpkins so the pumpkins sit on top of them. */}
      <img
        src={`${IMG}/ground-leaves-left@2x.png`}
        alt=""
        className={styles.groundLeft}
      />
      <img
        src={`${IMG}/ground-leaves-tree@2x.png`}
        alt=""
        className={styles.groundTree}
      />
      <img
        src={`${IMG}/ground-leaves-center@2x.png`}
        alt=""
        className={styles.groundCenter}
      />
      <img
        src={`${IMG}/ground-leaves-right@2x.png`}
        alt=""
        className={styles.groundRight}
      />

      {/* Low ground mist drifting slowly between the leaves and the pumpkins
        (pure CSS, no asset). */}
      <div className={styles.mist}>
        <span />
        <span />
      </div>

      <img
        src={`${IMG}/pumpkin-small-left.svg`}
        alt=""
        className={styles.pumpkinLeft}
      />
      <img
        src={`${IMG}/pumpkin-small-mid.svg`}
        alt=""
        className={styles.pumpkinMid}
      />
      {/* Jack-o'-lantern trio + its candies as ONE image (rendered from the
        Figma layers 7505:16153, 7534:47793/47774/48096/48187), so the candies
        can never drift away from the pumpkins at other screen widths. */}
      <img
        src={`${IMG}/pumpkin-patch@2x.png`}
        alt=""
        className={styles.pumpkinPatch}
      />
    </div>
  );
};

// The two spiders (tree + card) deliberately run at different speeds with
// different random rests (in whole sway cycles), so they never move in step.
const SPIDER_TREE = { speed: 0.85, restCycles: [1, 2], startDelayMs: 0 };
const SPIDER_CARD = { speed: 1.15, restCycles: [2, 4], startDelayMs: 1800 };

// Spider hanging from the AI bubble. Rendered over the tree with the tree's
// own box (treeClassName), so it stays on its bubble at every size.
export const HalloweenTreeDecor = ({ treeClassName }) => (
  <div className={`${treeClassName} ${styles.treeLayer}`} aria-hidden="true">
    <HangingSpider
      className={`${styles.spider} ${styles.spiderAi}`}
      {...SPIDER_TREE}
    />
  </div>
);

// Card-level layer: the only part shown on mobile. Cobweb in the card's
// top-left corner, a spider hanging from the top edge, a leaf resting on the
// top edge and a pumpkin at the bottom-left corner.
export const HalloweenCardDecor = () => (
  <div className={styles.cardLayer} aria-hidden="true">
    <img src={`${IMG}/cobweb-card.svg`} alt="" className={styles.cobwebCard} />
    <HangingSpider
      className={`${styles.spider} ${styles.spiderCard}`}
      {...SPIDER_CARD}
    />
    <img
      src={`${IMG}/leaf-maple-yellow.svg`}
      alt=""
      className={styles.leafRestCard}
    />
    <img
      src={`${IMG}/pumpkin-small-right.svg`}
      alt=""
      className={styles.pumpkinCard}
    />
  </div>
);
